import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from '../../contexts/LocationContext';
import LocationForm from './LocationForm';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit2, FiTrash2, FiMapPin, FiPhone, FiClock, FiSettings } = FiIcons;

const LocationManagement = () => {
  const { locations, deleteLocation } = useLocation();
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);

  const handleEdit = (location) => {
    setEditingLocation(location);
    setShowForm(true);
  };

  const handleDelete = (locationId) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      deleteLocation(locationId);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingLocation(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-playfair text-warm-brown mb-2">Location Management</h2>
          <p className="text-warm-brown/70">Manage your restaurant locations</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="text-sm" />
          Add Location
        </button>
      </div>

      {/* Locations Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {locations.map((location, index) => (
          <motion.div
            key={location.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-playfair text-warm-brown font-medium mb-2">
                    {location.name}
                  </h3>
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    location.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {location.isActive ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <SafeIcon icon={FiMapPin} className="text-warm-brown/50 mt-1 flex-shrink-0" />
                  <span className="text-warm-brown/80 text-sm">{location.address}</span>
                </div>
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiPhone} className="text-warm-brown/50" />
                  <span className="text-warm-brown/80 text-sm">{location.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiClock} className="text-warm-brown/50" />
                  <span className="text-warm-brown/80 text-sm">
                    Est. prep time: {location.settings.estimatedPrepTime} min
                  </span>
                </div>
              </div>

              {/* Working Hours Summary */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-warm-brown mb-2">Today's Hours</h4>
                <div className="text-sm text-warm-brown/70">
                  {(() => {
                    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                    const todayHours = location.workingHours[today];
                    return todayHours?.isOpen 
                      ? `${todayHours.open} - ${todayHours.close}`
                      : 'Closed';
                  })()}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(location)}
                  className="flex-1 bg-warm-brown/10 text-warm-brown py-2 px-3 rounded-lg hover:bg-warm-brown/20 transition-colors flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiEdit2} className="text-sm" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(location.id)}
                  className="flex-1 bg-red-50 text-red-600 py-2 px-3 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiTrash2} className="text-sm" />
                  Delete
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {locations.length === 0 && (
        <div className="text-center py-12">
          <SafeIcon icon={FiMapPin} className="text-6xl text-warm-brown/30 mx-auto mb-4" />
          <p className="text-warm-brown/70 text-lg">No locations found</p>
          <p className="text-warm-brown/50 text-sm">Add your first location to get started!</p>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <LocationForm
          location={editingLocation}
          onClose={resetForm}
        />
      )}
    </div>
  );
};

export default LocationManagement;